import { FC, useState } from "react"
import { View, ViewStyle, TextStyle, TouchableOpacity, Linking, Dimensions } from "react-native"
import Animated, { FadeIn, FadeOut, SlideInUp } from "react-native-reanimated"
import Icon from "react-native-vector-icons/MaterialIcons"

import { StarRating } from "@/components/StarRating"
import { Text } from "@/components/Text"
import { colors, spacing } from "@/theme"

interface RatingSectionProps {
  onRatingSubmit?: (rating: number) => void
  style?: ViewStyle
}

const { width: SCREEN_WIDTH } = Dimensions.get("window")
const isMobile = SCREEN_WIDTH < 768

const TRUSTPILOT_URL = "https://de.trustpilot.com/evaluate/holdeineplakette.de"
const FEEDBACK_EMAIL = "info@holdeineplakette.de"

export const RatingSection: FC<RatingSectionProps> = ({ onRatingSubmit, style }) => {
  const [rating, setRating] = useState(0)
  const [hasRated, setHasRated] = useState(false)

  const handleRatingChange = (newRating: number) => {
    // Rating changed by user
    setRating(newRating)
    if (newRating > 0 && !hasRated) {
      setHasRated(true)
      onRatingSubmit?.(newRating)
    }
  }

  const handleTrustpilotPress = () => {
    Linking.openURL(TRUSTPILOT_URL)
  }

  const handleEmailPress = () => {
    const subject = encodeURIComponent("Feedback zu meiner Bestellung")
    const body = encodeURIComponent(
      "Liebes HolDeinePlakette Team,\n\nIch möchte gerne Feedback zu meiner Bestellung geben:\n\n",
    )
    const mailtoUrl = `mailto:${FEEDBACK_EMAIL}?subject=${subject}&body=${body}`
    Linking.openURL(mailtoUrl)
  }

  // Rendering rating section

  return (
    <Animated.View entering={SlideInUp.springify().damping(20)} style={[$container, style]}>
      <View style={$card}>
        {/* Initial Rating Request */}
        {!hasRated && (
          <Animated.View entering={FadeIn}>
            <Text style={$title} preset="heading">
              Wie hat es dir gefallen?
            </Text>
            <Text style={$subtitle}>Deine Meinung ist uns wichtig ❤️</Text>
            <StarRating
              rating={rating}
              onRatingChange={handleRatingChange}
              size={isMobile ? 40 : 48}
              style={$starRating}
            />
          </Animated.View>
        )}

        {/* Thank You Message for 5 Stars */}
        {hasRated && rating === 5 && (
          <Animated.View entering={FadeIn.delay(300)} exiting={FadeOut} style={$feedbackContainer}>
            <View style={$iconContainer}>
              <Icon name="favorite" size={36} color={colors.primary} />
            </View>
            <Text style={$thankYouTitle} preset="heading">
              Danke dir für 5 Sterne! 🙌
            </Text>
            <Text style={$feedbackText}>Mega, dass du zufrieden warst – das treibt uns an!</Text>
            <Text style={$feedbackText}>
              Hättest du 1 Min für eine Bewertung auf Trustpilot? Das hilft uns enorm weiter:
            </Text>
            <TouchableOpacity
              style={$primaryButton}
              onPress={handleTrustpilotPress}
              activeOpacity={0.8}
            >
              <Icon name="star" size={18} color={colors.surface} />
              <Text style={$buttonText}>Auf Trustpilot bewerten</Text>
            </TouchableOpacity>
            <Text style={[$feedbackText, { marginTop: spacing.xs }]}>
              Danke fürs Vertrauen 🚗✨
            </Text>
          </Animated.View>
        )}

        {/* Feedback Request for 1-2 Stars */}
        {hasRated && rating >= 1 && rating <= 2 && (
          <Animated.View entering={FadeIn.delay(300)} exiting={FadeOut} style={$feedbackContainer}>
            <View style={$iconContainer}>
              <Icon name="sentiment-dissatisfied" size={36} color={colors.warning} />
            </View>
            <Text style={$sorryTitle} preset="heading">
              Schade, dass du unzufrieden warst 😟
            </Text>
            <Text style={$feedbackText}>
              Danke für dein ehrliches Feedback – das nehmen wir uns zu Herzen. Wir geben jeden Tag
              alles, um exzellenten Service zu liefern – und da zählt deine Meinung.
            </Text>
            <Text style={$feedbackText}>
              Magst du uns kurz schreiben, was schiefgelaufen ist? → {FEEDBACK_EMAIL}
            </Text>
            <Text style={$feedbackText}>
              Wir freuen uns, wenn du uns nochmal eine Chance gibst.
            </Text>
            <TouchableOpacity
              style={$secondaryButton}
              onPress={handleEmailPress}
              activeOpacity={0.8}
            >
              <Icon name="email" size={18} color={colors.primary} />
              <Text style={$secondaryButtonText}>Feedback per E-Mail senden</Text>
            </TouchableOpacity>
          </Animated.View>
        )}

        {/* Feedback Request for 3-4 Stars */}
        {hasRated && rating >= 3 && rating <= 4 && (
          <Animated.View entering={FadeIn.delay(300)} exiting={FadeOut} style={$feedbackContainer}>
            <View style={$iconContainer}>
              <Icon name="stars" size={36} color={colors.primary} />
            </View>
            <Text style={$sorryTitle} preset="heading">
              Nächstes Mal kommen wir hoffentlich auf die 5 Sterne ⭐
            </Text>
            <Text style={$feedbackText}>
              Danke für dein ehrliches Feedback – das nehmen wir uns zu Herzen. Wir geben jeden Tag
              alles, um exzellenten Service zu liefern – und da zählt deine Meinung.
            </Text>
            <Text style={$feedbackText}>
              Magst du uns kurz schreiben, womit du nicht zu 100% zufrieden warst? →{" "}
              {FEEDBACK_EMAIL}
            </Text>
            <Text style={$feedbackText}>Wir freuen uns auf deinen nächsten Besuch.</Text>
            <TouchableOpacity
              style={$secondaryButton}
              onPress={handleEmailPress}
              activeOpacity={0.8}
            >
              <Icon name="email" size={18} color={colors.primary} />
              <Text style={$secondaryButtonText}>Feedback per E-Mail senden</Text>
            </TouchableOpacity>
          </Animated.View>
        )}
      </View>
    </Animated.View>
  )
}

// Container styles
const $container: ViewStyle = {
  width: "100%",
}

const $card: ViewStyle = {
  backgroundColor: colors.surface,
  borderRadius: 16,
  paddingTop: spacing.sm,
  paddingHorizontal: spacing.sm,
  shadowColor: colors.palette.neutral900,
  shadowOffset: { width: 0, height: 2 },
  shadowOpacity: 0.1,
  shadowRadius: 8,
  elevation: 4,
}

// Text styles
const $title: TextStyle = {
  textAlign: "center",
  marginBottom: spacing.xxs,
  color: colors.text,
  fontSize: isMobile ? 18 : 20,
}

const $subtitle: TextStyle = {
  textAlign: "center",
  color: colors.textDim,
  marginBottom: spacing.xs,
  fontSize: isMobile ? 13 : 14,
}

const $thankYouTitle: TextStyle = {
  textAlign: "center",
  marginBottom: spacing.xs,
  color: colors.text,
  fontSize: isMobile ? 18 : 20,
}

const $sorryTitle: TextStyle = {
  textAlign: "center",
  marginBottom: spacing.xs,
  color: colors.text,
  fontSize: isMobile ? 18 : 20,
}

const $feedbackText: TextStyle = {
  textAlign: "center",
  color: colors.textDim,
  marginBottom: spacing.sm,
  lineHeight: 20,
  fontSize: isMobile ? 13 : 14,
}

// Component styles
const $starRating: ViewStyle = {
  justifyContent: "center",
  marginTop: spacing.xs,
  marginBottom: spacing.sm,
}

const $feedbackContainer: ViewStyle = {
  alignItems: "center",
}

const $iconContainer: ViewStyle = {
  marginBottom: spacing.xs,
}

// Button styles
const $primaryButton: ViewStyle = {
  flexDirection: "row",
  alignItems: "center",
  backgroundColor: colors.primary,
  paddingHorizontal: spacing.md,
  paddingVertical: spacing.xs,
  borderRadius: 12,
  gap: spacing.xxs,
  shadowColor: colors.primary,
  shadowOffset: { width: 0, height: 4 },
  shadowOpacity: 0.2,
  shadowRadius: 8,
  elevation: 4,
}

const $secondaryButton: ViewStyle = {
  flexDirection: "row",
  alignItems: "center",
  backgroundColor: colors.surface,
  borderWidth: 2,
  borderColor: colors.primary,
  paddingHorizontal: spacing.md,
  paddingVertical: spacing.xs,
  borderRadius: 12,
  gap: spacing.xxs,
}

const $buttonText: TextStyle = {
  color: colors.surface,
  fontSize: 14,
  fontWeight: "600",
}

const $secondaryButtonText: TextStyle = {
  color: colors.primary,
  fontSize: 14,
  fontWeight: "600",
}
